// OrderQuickView — click-a-row modal preview of an order's full POD
// chain-of-custody, styled after CNC's pharmacy portal preview
// (cncdelivery.com/portal/dashboard row click).
//
// Sections (top to bottom):
//   1. Patient name + address + close X
//   2. Compliance chips: Geofence / Barcode / Photos
//   3. Timeline (order → assigned → dispatched → delivered)
//   4. Metadata 2-column grid: Order ID / Driver / Delivered At /
//      Cold Chain / GPS / Geofence / Barcode / Received By
//   5. Photo thumbnails (opens fullscreen on click)
//   6. Delivery note strip
//   7. Download POD as PDF — hits /api/audit/pdf with order_id
//
// Deferred to a follow-up PR:
//   - Mapbox / MapTiler map with house pin + driver GPS pin
//   - Real audit_events-driven timeline (v1 derives from order +
//     delivery timestamps)
//   - Signature thumbnail (POD signature preview)

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Alert } from '../ui/Alert';

// -----------------------------------------------------------------------
// Types

type QuickViewOrder = {
  id: string;
  order_number: string;
  patient_name: string | null;
  status: string;
  created_at: string;
  updated_at: string;
};

type QuickViewStop = {
  address: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  latitude: number | null;
  longitude: number | null;
};

type QuickViewDriver = {
  full_name: string | null;
  email: string;
};

type QuickViewDelivery = {
  id: string;
  delivered_at: string | null;
  delivery_lat: number | null;
  delivery_lng: number | null;
  delivery_geofence_distance_meters: number | null;
  delivery_geofence_overridden: boolean;
  override_reason: string | null;
  cold_chain_required: boolean;
  created_at: string;
};

type QuickViewPod = {
  id: string;
  barcode_scanned: boolean;
  barcode_skip_reason: string | null;
  photo_paths: string[];
  driver_note: string | null;
  recipient_label: string | null;
  recipient_name: string | null;
  captured_at: string;
};

type QuickViewData = {
  order: QuickViewOrder;
  stop: QuickViewStop | null;
  driver: QuickViewDriver | null;
  delivery: QuickViewDelivery | null;
  pod: QuickViewPod | null;
};

// -----------------------------------------------------------------------
// Title-case helper (mirrors the one in the Orders route file).

const TITLE_CASE_LOWER = new Set(['of', 'the', 'and', 'a', 'an', 'in', 'on']);
function titleCase(str: string): string {
  return str.toLowerCase().replace(/[\p{L}\p{N}']+/gu, (word, idx) => {
    if (idx > 0 && TITLE_CASE_LOWER.has(word)) return word;
    return word.charAt(0).toUpperCase() + word.slice(1);
  });
}

// snake_case → "Snake Case" for enum values the driver flow persists
// (e.g. drop_location:front_door, recipient_label:building_staff).
function humanizeSnake(v: string): string {
  return titleCase(v.replace(/_/g, ' '));
}

// Driver notes ship as `drop_location:<value>[ | <free text>]`. Show
// the pharmacy a friendly "Front Door" instead of the raw enum. If a
// free-text tail exists (rare — most drops are just the enum), append
// it after " — " so the reader sees both.
function formatDriverNote(note: string): string {
  const trimmed = note.trim();
  if (!trimmed) return '—';
  const match = trimmed.match(/^drop_location:([^\s|]+)(?:\s*\|\s*(.+))?$/i);
  if (!match) return trimmed;
  const enumValue = match[1] ?? '';
  const tail = match[2]?.trim();
  const pretty = enumValue ? humanizeSnake(enumValue) : trimmed;
  return tail ? `${pretty} — ${tail}` : pretty;
}

// -----------------------------------------------------------------------
// Data fetch

async function fetchQuickView(orderId: string): Promise<QuickViewData> {
  // Single joined query. RLS + tenant scoping is enforced downstream
  // by the orders table's policies — the caller's JWT is attached
  // automatically by the supabase client.
  const { data, error } = await supabase
    .from('orders')
    .select(
      `id, order_number, patient_name, status, created_at, updated_at,
       dropoff_stop:stops!orders_dropoff_stop_id_fkey(
         address, city, state, zip, latitude, longitude,
         route:routes!stops_route_id_fkey(
           driver:users!routes_driver_id_fkey(full_name, email)
         )
       ),
       deliveries(id, delivered_at, delivery_lat, delivery_lng,
                  delivery_geofence_distance_meters,
                  delivery_geofence_overridden, override_reason,
                  cold_chain_required, created_at),
       pod_records(id, barcode_scanned, barcode_skip_reason,
                   photo_paths, driver_note, recipient_label,
                   recipient_name, captured_at)`,
    )
    .eq('id', orderId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) throw new Error('Order not found or not visible to you.');

  const row = data as unknown as {
    id: string;
    order_number: string;
    patient_name: string | null;
    status: string;
    created_at: string;
    updated_at: string;
    dropoff_stop: {
      address: string;
      city: string | null;
      state: string | null;
      zip: string | null;
      latitude: number | null;
      longitude: number | null;
      route: { driver: QuickViewDriver | null } | null;
    } | null;
    deliveries: QuickViewDelivery[];
    pod_records: QuickViewPod[];
  };

  return {
    order: {
      id: row.id,
      order_number: row.order_number,
      patient_name: row.patient_name,
      status: row.status,
      created_at: row.created_at,
      updated_at: row.updated_at,
    },
    stop: row.dropoff_stop
      ? {
          address: row.dropoff_stop.address,
          city: row.dropoff_stop.city,
          state: row.dropoff_stop.state,
          zip: row.dropoff_stop.zip,
          latitude: row.dropoff_stop.latitude,
          longitude: row.dropoff_stop.longitude,
        }
      : null,
    driver: row.dropoff_stop?.route?.driver ?? null,
    delivery: row.deliveries[0] ?? null,
    pod: row.pod_records[0] ?? null,
  };
}

// -----------------------------------------------------------------------
// Photo thumbnail loader — resolves signed URLs for the pod-photos
// bucket. Photos are PHI-adjacent so the bucket is RLS-gated; we
// generate one-off signed URLs valid for 60s per open.

async function signPhotoUrls(paths: string[]): Promise<Array<{ path: string; url: string }>> {
  if (paths.length === 0) return [];
  const results = await Promise.all(
    paths.map(async (path) => {
      const { data, error } = await supabase.storage
        .from('pod-photos')
        .createSignedUrl(path, 60);
      return { path, url: error || !data ? '' : data.signedUrl };
    }),
  );
  return results.filter((r) => r.url !== '');
}

// -----------------------------------------------------------------------
// Component

type Props = {
  orderId: string | null;
  onClose: () => void;
  onOpenFull?: (orderId: string) => void;
};

export function OrderQuickView({ orderId, onClose, onOpenFull }: Props): JSX.Element | null {
  const [data, setData] = useState<QuickViewData | null>(null);
  const [photos, setPhotos] = useState<Array<{ path: string; url: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  // In-modal photo lightbox — clicking a thumbnail opens the full-
  // size image on a black scrim over the modal. Escape / backdrop
  // click closes; matches CNC's portal preview behavior (no new
  // tab, no browser back-button hopscotch).
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  useEffect(() => {
    // Also let ESC close the lightbox without also closing the
    // underlying modal. The native <dialog> ESC handler runs after
    // this, so stopping propagation is important.
    if (!lightboxUrl) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setLightboxUrl(null);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [lightboxUrl]);

  useEffect(() => {
    if (!orderId) {
      setData(null);
      setPhotos([]);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const d = await fetchQuickView(orderId);
        if (cancelled) return;
        setData(d);
        // Kick off photo signing in parallel — the modal renders
        // before signed URLs arrive, showing skeleton tiles.
        if (d.pod?.photo_paths?.length) {
          const signed = await signPhotoUrls(d.pod.photo_paths);
          if (!cancelled) setPhotos(signed);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const patientTitle = useMemo(() => {
    if (!data?.order.patient_name) return 'Order';
    return titleCase(data.order.patient_name);
  }, [data]);

  const handleDownloadPdf = async () => {
    if (!orderId) return;
    setDownloading(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error('Session expired. Sign in again.');
      const res = await fetch('/api/audit/pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ order_id: orderId }),
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`PDF export failed (${res.status}): ${body.slice(0, 200)}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pod-${data?.order.order_number ?? orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDownloading(false);
    }
  };

  if (!orderId) return null;

  const footer =
    onOpenFull && orderId ? (
      <>
        <button
          type="button"
          onClick={() => onOpenFull(orderId)}
          className="text-xs font-medium text-primary hover:underline"
        >
          View full details →
        </button>
      </>
    ) : null;

  return (
    <Modal
      open={orderId !== null}
      onClose={onClose}
      title={patientTitle}
      widthClass="max-w-2xl"
      footer={footer}
    >
      {loading && !data ? (
        <div className="py-8 text-center text-sm text-ink-2">Loading…</div>
      ) : null}

      {error ? (
        <div className="mb-3">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {data ? (
        <div className="space-y-4 text-xs">
          {/* Address strip */}
          <div className="text-ink-2">
            {data.stop
              ? `${titleCase(data.stop.address)}${
                  data.stop.city ? `, ${titleCase(data.stop.city)}` : ''
                }${data.stop.state ? ` ${data.stop.state}` : ''}${
                  data.stop.zip ? ` ${data.stop.zip}` : ''
                }`
              : '—'}
          </div>

          {/* Compliance chips */}
          <ComplianceChips data={data} />

          {/* Timeline */}
          <Timeline data={data} />

          {/* Metadata grid */}
          <MetadataGrid data={data} />

          {/* Photos — thumbnails open a fullscreen lightbox on the
              same page. Buttons (not anchors) so the click stays
              in-app; a target="_blank" anchor would round-trip
              through a new tab. */}
          {photos.length > 0 ? (
            <div className="grid grid-cols-2 gap-2">
              {photos.map((p, i) => (
                <button
                  type="button"
                  key={p.path}
                  onClick={() => setLightboxUrl(p.url)}
                  aria-label={`Open delivery photo ${i + 1}`}
                  className="relative block overflow-hidden rounded-md border border-border focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <span className="absolute left-2 top-2 rounded bg-ink/70 px-2 py-0.5 text-[10px] font-semibold text-white">
                    Photo {i + 1}
                  </span>
                  <img
                    src={p.url}
                    alt={`Delivery photo ${i + 1}`}
                    className="h-40 w-full object-cover"
                  />
                </button>
              ))}
            </div>
          ) : data.pod?.photo_paths.length ? (
            <div className="text-ink-3">Photos loading…</div>
          ) : null}

          {/* Delivery note — driver_note is persisted as the raw
              enum ("drop_location:front_door"); render pharmacist-
              friendly via formatDriverNote. */}
          {data.pod?.driver_note ? (
            <div className="rounded-md bg-bg-soft px-3 py-2 text-ink-2">
              <span className="font-semibold text-ink-3">DELIVERY NOTE:</span>{' '}
              {formatDriverNote(data.pod.driver_note)}
            </div>
          ) : null}

          {/* PDF download */}
          <div>
            <Button onClick={handleDownloadPdf} disabled={downloading || !data.pod}>
              {downloading ? 'Building PDF…' : 'Download POD as PDF'}
            </Button>
            {!data.pod ? (
              <p className="mt-1 text-[11px] text-ink-3">
                POD PDF is available once the delivery has been captured.
              </p>
            ) : null}
          </div>

          {/* Fullscreen photo lightbox. position:fixed relative to
              the viewport (works even nested inside <dialog>). Click
              anywhere on the scrim (including the image bg area) or
              the X to close. ESC also closes via the effect above. */}
          {lightboxUrl ? (
            <div
              role="dialog"
              aria-label="Delivery photo"
              onClick={() => setLightboxUrl(null)}
              className="fixed inset-0 z-[999] flex items-center justify-center bg-black/95 p-6"
            >
              <img
                src={lightboxUrl}
                alt="Delivery photo (fullscreen)"
                className="max-h-full max-w-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxUrl(null);
                }}
                aria-label="Close photo"
                className="absolute right-6 top-6 rounded-full bg-white/15 p-2 text-white hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

// -----------------------------------------------------------------------
// Sub-components

function ComplianceChips({ data }: { data: QuickViewData }): JSX.Element | null {
  const { delivery, pod } = data;
  if (!pod && !delivery) return null;
  const chips: Array<{ label: string; tone: 'ok' | 'warn' | 'muted' }> = [];

  // Geofence
  if (delivery) {
    const overridden = delivery.delivery_geofence_overridden;
    const dist = delivery.delivery_geofence_distance_meters;
    const distFt = dist != null ? Math.round(dist * 3.28084) : null;
    chips.push({
      label: overridden
        ? `Geofence: Overridden${distFt != null ? ` (${distFt} ft)` : ''}`
        : `Geofence: Verified${distFt != null ? ` (${distFt} ft)` : ''}`,
      tone: overridden ? 'warn' : 'ok',
    });
  }

  // Barcode
  if (pod) {
    chips.push({
      label: pod.barcode_scanned
        ? 'Barcode: Scanned'
        : `Barcode: Skipped${pod.barcode_skip_reason ? ` (${pod.barcode_skip_reason})` : ''}`,
      tone: pod.barcode_scanned ? 'ok' : 'warn',
    });
  }

  // Photos
  if (pod) {
    const count = pod.photo_paths.length;
    chips.push({
      label: `Photos: ${count} of ${Math.max(count, 2)} min`,
      tone: count >= 2 ? 'ok' : count === 0 ? 'warn' : 'muted',
    });
  }

  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((c) => (
        <span
          key={c.label}
          className={
            'inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ' +
            (c.tone === 'ok'
              ? 'bg-signal-green-tint text-signal-green'
              : c.tone === 'warn'
                ? 'bg-signal-amber-tint text-signal-amber'
                : 'bg-bg-soft text-ink-2')
          }
        >
          {c.label}
        </span>
      ))}
    </div>
  );
}

function Timeline({ data }: { data: QuickViewData }): JSX.Element {
  // v1 timeline derived from row timestamps. Real audit_events feed
  // comes in a follow-up.
  const events: Array<{ label: string; sub?: string; when: string | null }> = [];
  events.push({
    label: 'Order Received',
    when: data.order.created_at,
  });
  if (data.driver) {
    events.push({
      label: `Assigned to ${firstName(data.driver.full_name ?? data.driver.email)}`,
      when: data.delivery?.created_at ?? null,
    });
  }
  if (data.delivery?.created_at) {
    events.push({
      label: 'Route Dispatched',
      when: data.delivery.created_at,
    });
  }
  if (data.delivery?.delivered_at) {
    events.push({
      label: 'Delivered',
      when: data.delivery.delivered_at,
    });
  }
  return (
    <div className="rounded-md bg-bg-soft p-3">
      <ol className="space-y-2">
        {events.map((e) => (
          <li key={e.label} className="flex items-start gap-2.5">
            <span
              aria-hidden="true"
              className="mt-1 inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-signal-green text-white"
            >
              <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" fill="none">
                <path d="M2 6l3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <div>
              <div className="font-semibold text-ink">{e.label}</div>
              {e.when ? (
                <div className="text-ink-3">
                  {new Date(e.when).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function MetadataGrid({ data }: { data: QuickViewData }): JSX.Element {
  const rows: Array<{ label: string; value: string }> = [
    { label: 'ORDER ID', value: `#${data.order.order_number}` },
    {
      label: 'DRIVER',
      value: data.driver
        ? firstName(data.driver.full_name ?? data.driver.email)
        : '—',
    },
    {
      label: 'DELIVERED AT',
      value: data.delivery?.delivered_at
        ? new Date(data.delivery.delivered_at).toLocaleString(undefined, {
            hour: 'numeric',
            minute: '2-digit',
          })
        : '—',
    },
    {
      label: 'COLD CHAIN',
      value: data.delivery?.cold_chain_required ? 'Yes' : 'No',
    },
    {
      label: 'GPS LOCATION',
      value:
        data.delivery?.delivery_lat != null && data.delivery.delivery_lng != null
          ? `${data.delivery.delivery_lat.toFixed(6)}, ${data.delivery.delivery_lng.toFixed(6)}`
          : '—',
    },
    {
      label: 'GEOFENCE',
      value: data.delivery
        ? data.delivery.delivery_geofence_overridden
          ? `Overridden${
              data.delivery.override_reason ? ` — ${data.delivery.override_reason}` : ''
            }`
          : 'Verified'
        : '—',
    },
    {
      label: 'BARCODE',
      value: data.pod
        ? data.pod.barcode_scanned
          ? 'Matched'
          : `Skipped${data.pod.barcode_skip_reason ? ` (${data.pod.barcode_skip_reason})` : ''}`
        : '—',
    },
    {
      label: 'RECEIVED BY',
      value: data.pod?.recipient_label
        ? humanizeSnake(data.pod.recipient_label)
        : '—',
    },
  ];
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
      {rows.map((r) => (
        <div key={r.label}>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-3">
            {r.label}
          </div>
          <div className="mt-0.5 text-ink">{r.value}</div>
        </div>
      ))}
    </div>
  );
}

function firstName(source: string): string {
  const token = source.trim().split(/\s+/)[0] ?? source;
  const at = token.indexOf('@');
  return at >= 0 ? token.slice(0, at) : token;
}
